import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getAffiliateInvitationsByUsername, acceptCampaign, rejectCampaign, addYouTubeMedia, addInstagramMedia, getInvitation } from '../services/affiliateService';
import { INFLUENCER_BASE } from "../constants";
import {
    FaCheckCircle, FaTimesCircle, FaExternalLinkAlt,
    FaDownload,
    FaTimes,
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "react-datepicker/dist/react-datepicker.css";
import LoadingSpinner from '../../components/LoadingSpinner';
import debounce from 'lodash/debounce';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import ComingSoonModal from '../../utils/dialogUtils';
import { DashboardStats } from './yourcampaign/DashboardStats';
import { CampaignSection } from './yourcampaign/CampaignSection';
import { FilterSection } from './yourcampaign/FilterSection';
import { CommissionDetailsModal } from './yourcampaign/CommissionDetailsModal';
import { BrandDetailsModal } from './yourcampaign/BrandDetailsModal';

// Modal components are imported from yourcampaign folder

const AffiliateDashboard = () => {
    // const dispatch = useDispatch();
    const user = useSelector(state => state.user.data);
    const [invitations, setInvitations] = useState({
        pending: {},
        accepted: {},
        rejected: {},
        expired: {}
    });
    const [counts, setCounts] = useState({
        pending: 0,
        accepted: 0,
        rejected: 0,
        expired: 0
    });
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [brandFilter, setBrandFilter] = useState('');
    const [availableCompanies, setAvailableCompanies] = useState([]);
    const [availableBrands, setAvailableBrands] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [copied, setCopied] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [activeFilters, setActiveFilters] = useState([]);
    const [showComingSoonModal, setShowComingSoonModal] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState({
        isOpen: false,
        campaignId: null,
        token: null,
        action: null,
        isSubmitting: false,
    });
    const confirmationSubmitLockRef = useRef(false);
    const [emailInviteModal, setEmailInviteModal] = useState({
        isOpen: false,
        token: null,
        invitation: null,
        error: null,
        loading: false,
    });
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [youtubeUrls, setYoutubeUrls] = useState([]);
    const [newYoutubeLink, setNewYoutubeLink] = useState('');
    const [mediaError, setMediaError] = useState('');
    const [mediaSuccess, setMediaSuccess] = useState('');
    const [mediaLoading, setMediaLoading] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [selectedMediaType, setSelectedMediaType] = useState('youtube'); // 'youtube' or 'instagram'
    const [instagramUrls, setInstagramUrls] = useState([]);
    const [newInstagramLink, setNewInstagramLink] = useState('');
    const [commissionModal, setCommissionModal] = useState({
        isOpen: false,
        commission: null,
        campaignName: '',
        campaignType: '',
        agreementStatus: ''
    });
    const [brandDetailsModal, setBrandDetailsModal] = useState({
        isOpen: false,
        companyName: '',
        brandName: '',
        companyEmail: '',
        companyPhone: ''
    });

    const navigate = useNavigate();
    const location = useLocation();

    const handleAddMedia = async () => {
        setMediaError('');
        setMediaSuccess('');
        setMediaLoading(true);

        try {
            // Get links based on selected media type
            let allLinks = [];
            if (selectedMediaType === 'youtube') {
                allLinks = [...youtubeUrls, newYoutubeLink].filter(url => url.trim() !== '');
            } else {
                allLinks = [...instagramUrls, newInstagramLink].filter(url => url.trim() !== '');
            }

            if (allLinks.length === 0) {
                setMediaError(`Please enter at least one ${selectedMediaType === 'youtube' ? 'YouTube' : 'Instagram'} link.`);
                setMediaLoading(false);
                return;
            }

            // Use the selected campaign
            if (!selectedCampaign) {
                setMediaError('No campaign selected. Please try again.');
                setMediaLoading(false);
                return;
            }

            const campaignId = selectedCampaign._id;

            let res;
            if (selectedMediaType === 'youtube') {
                res = await addYouTubeMedia(allLinks, campaignId);
            } else {
                res = await addInstagramMedia(allLinks, campaignId);
            }

            if (res.success) {
                const mediaCount = allLinks.length;
                const mediaType = selectedMediaType === 'youtube' ? 'YouTube' : 'Instagram';
                toast.success(`Successfully submitted ${mediaCount} ${mediaType} media item${mediaCount !== 1 ? 's' : ''} for approval! Redirecting to dashboard...`, {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                setMediaSuccess(`${mediaType} media details submitted for approval!`);

                // Reset form based on media type
                if (selectedMediaType === 'youtube') {
                    setYoutubeUrls([]);
                    setNewYoutubeLink('');
                } else {
                    setInstagramUrls([]);
                    setNewInstagramLink('');
                }

                setSelectedCampaign(null);
                setIsDialogOpen(false);
                setTimeout(() => {
                    if (selectedMediaType === 'youtube') {
                        navigate(`${INFLUENCER_BASE}/dashboard/youtube-media-dashboard`);
                    } else {
                        navigate(`${INFLUENCER_BASE}/dashboard/insta-media-dashboard`);
                    }
                }, 2000);
            } else {
                toast.error(res.message || 'Failed to add media.', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                setMediaError(res.message || 'Failed to add media.');
            }
        } catch (err) {
            console.error('Media add error:', err.response?.data || err.message || err);

            // Handle YouTube session expiration
            if (selectedMediaType === 'youtube' && (err.requiresReconnection || err.response?.data?.requiresReconnection)) {
                const errorMsg = 'Your YouTube session has expired. Please reconnect your YouTube account to continue.';

                toast.error(errorMsg, {
                    position: "top-right",
                    autoClose: 8000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                setMediaError(errorMsg);

                setTimeout(() => {
                    if (window.confirm('Would you like to reconnect your YouTube account now?')) {
                        navigate(`${INFLUENCER_BASE}/dashboard/connect-social-media`);
                    }
                }, 2000);

                return;
            }

            // Handle Instagram session expiration
            if (selectedMediaType === 'instagram' && (err.requiresReconnection || err.response?.data?.requiresReconnection)) {
                const errorMsg = 'Your Instagram session has expired. Please reconnect your Instagram account to continue.';

                toast.error(errorMsg, {
                    position: "top-right",
                    autoClose: 8000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                setMediaError(errorMsg);

                setTimeout(() => {
                    if (window.confirm('Would you like to reconnect your Instagram account now?')) {
                        navigate(`${INFLUENCER_BASE}/dashboard/connect-social-media`);
                    }
                }, 2000);

                return;
            }

            // Handle Instagram permission errors (Basic Display API limitations)
            if (selectedMediaType === 'instagram' && (err.permissionDenied || err.response?.data?.error === 'instagram_permission_denied')) {
                const errorMsg = 'This post is not accessible with your current Instagram permissions. Please ensure you are adding your own posts.';

                toast.error(errorMsg, {
                    position: "top-right",
                    autoClose: 8000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                setMediaError(errorMsg);
                return;
            }

            // Handle Instagram no posts found error
            if (selectedMediaType === 'instagram' && (err.noPostsFound || err.response?.data?.error === 'no_posts_found')) {
                const errorMsg = 'No Instagram posts could be found. Please ensure you are adding your own posts and they are publicly accessible.';

                toast.error(errorMsg, {
                    position: "top-right",
                    autoClose: 8000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                setMediaError(errorMsg);
                return;
            }

            // Handle unauthorized videos/posts error specifically
            if (err.response?.data?.unauthorizedVideos || err.response?.data?.unauthorizedPosts) {
                const unauthorizedItems = err.response.data.unauthorizedVideos || err.response.data.unauthorizedPosts;
                const unauthorizedTitles = unauthorizedItems.map(v => v.title).join(', ');
                const mediaType = selectedMediaType === 'youtube' ? 'videos' : 'posts';
                const channelType = selectedMediaType === 'youtube' ? 'channel' : 'account';

                const errorMsg = `You can only add ${mediaType} from your own ${selectedMediaType === 'youtube' ? 'YouTube' : 'Instagram'} ${channelType}. The following ${mediaType} do not belong to your connected ${channelType}: ${unauthorizedTitles}`;

                toast.error(errorMsg, {
                    position: "top-right",
                    autoClose: 8000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                setMediaError(errorMsg);

                // Remove unauthorized items from the list
                const unauthorizedIds = unauthorizedItems.map(v => v.id || v.postId);
                if (selectedMediaType === 'youtube') {
                    const filteredUrls = youtubeUrls.filter(url => {
                        const videoId = url.match(/(?:v=|\/(shorts|embed|watch)\?v=|youtu\.be\/|\/shorts\/)([a-zA-Z0-9_-]{11})/)?.[2];
                        return !unauthorizedIds.includes(videoId);
                    });
                    setYoutubeUrls(filteredUrls);

                    const newLinkVideoId = newYoutubeLink.match(/(?:v=|\/(shorts|embed|watch)\?v=|youtu\.be\/|\/shorts\/)([a-zA-Z0-9_-]{11})/)?.[2];
                    if (unauthorizedIds.includes(newLinkVideoId)) {
                        setNewYoutubeLink('');
                    }
                } else {
                    const filteredUrls = instagramUrls.filter(url => {
                        const postId = url.match(/\/p\/([a-zA-Z0-9_-]+)/)?.[1];
                        return !unauthorizedIds.includes(postId);
                    });
                    setInstagramUrls(filteredUrls);

                    const newLinkPostId = newInstagramLink.match(/\/p\/([a-zA-Z0-9_-]+)/)?.[1];
                    if (unauthorizedIds.includes(newLinkPostId)) {
                        setNewInstagramLink('');
                    }
                }
            } else {
                const errorMsg = err.response?.data?.message || err.message || 'Failed to add media.';
                toast.error(errorMsg, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                setMediaError(errorMsg);
            }
        } finally {
            setMediaLoading(false);
        }
    };

    const debouncedSearch = useMemo(() => {
        return debounce((value) => {
            setSearchTerm(value);
            setPage(1);
        }, 300);
    }, [setPage]);
      
      useEffect(() => {
        return () => {
          debouncedSearch.cancel(); // 👈 VERY IMPORTANT
        };
      }, [debouncedSearch]);

    // Enhanced filter handling
    const handleFilterChange = (type, value) => {
        const newFilters = [...activeFilters];
        const existingFilterIndex = newFilters.findIndex(f => f.type === type);

        if (existingFilterIndex >= 0) {
            if (value) {
                newFilters[existingFilterIndex].value = value;
            } else {
                newFilters.splice(existingFilterIndex, 1);
            }
        } else if (value) {
            newFilters.push({ type, value });
        }

        setActiveFilters(newFilters);
    };



    // Export functionality
    const handleExport = () => {
        setShowComingSoonModal(true);
    };

    useEffect(() => {
        fetchInvitations();

    }, [user, searchTerm, companyFilter, brandFilter, page, startDate, endDate]);




    const fetchInvitations = useCallback(async () => {
        try {
            const username = String(user?.username || "").trim().replace(/^@/, "");
            if (!username) {
                console.warn('User username not available, skipping fetch');
                setLoading(false);
                return;
            }

            setLoading(true);
            const response = await getAffiliateInvitationsByUsername(username, {
                search: searchTerm,
                companyFilter,
                brandFilter,
                page,
                limit,
                startDate: startDate?.toISOString(),
                endDate: endDate?.toISOString()
            });

            if (response.success && response.data) {
                setInvitations(response.data.invitations);
                setCounts(response.data.counts);
                setTotalPages(response.data.pagination.pages);

                // Extract unique companies and brands for filters
                const companies = new Set();
                const brands = new Set();

                Object.values(response.data.invitations).forEach(statusGroup => {
                    Object.entries(statusGroup).forEach(([companyId, company]) => {
                        companies.add(JSON.stringify({ id: companyId, name: company.companyName }));
                        Object.entries(company.brands).forEach(([brandId, brand]) => {
                            brands.add(JSON.stringify({ id: brandId, name: brand.brandName }));
                        });
                    });
                });


                setAvailableCompanies(Array.from(companies).map(c => JSON.parse(c)));
                setAvailableBrands(Array.from(brands).map(b => JSON.parse(b)));
            } else {
                setError('Failed to load your campaigns.');
                toast.error('Failed to load your campaigns. Please try again later.');
            }
        } catch (err) {
            console.error('Error fetching affiliate invitations:', err);
            setError('Failed to load your campaigns.');
            toast.error('Failed to load your campaigns. Please try again later.');
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    },[
        user?.username,
        searchTerm,
        companyFilter,
        brandFilter,
        page,
        limit,
        startDate,
        endDate,
      ]);

    const openConfirmationModal = (token, campaignId, action) => {
        confirmationSubmitLockRef.current = false;
        setConfirmationModal({
            isOpen: true,
            token,
            campaignId,
            action,
            isSubmitting: false,
        });
    };

    const confirmAction = async () => {
        if (confirmationSubmitLockRef.current || confirmationModal.isSubmitting) return;
        const { token, campaignId, action } = confirmationModal;

        confirmationSubmitLockRef.current = true;
        setConfirmationModal((prev) => ({ ...prev, isSubmitting: true }));

        try {
            const fn = action === 'accept' ? acceptCampaign : rejectCampaign;
            const response = await fn(token, campaignId);
            toast.success(response.message || `Campaign ${action}ed successfully`);

            await fetchInvitations();
        } catch (err) {
            toast.error(err.message || `Failed to ${action} campaign`);
        } finally {
            confirmationSubmitLockRef.current = false;
            setConfirmationModal({
                isOpen: false,
                token: null,
                campaignId: null,
                action: null,
                isSubmitting: false,
            });
        }
    };

    // If user arrives via an email invite link, show the invitation inline.
    useEffect(() => {
        const params = new URLSearchParams(location.search || '');
        const inviteToken = params.get('inviteToken');
        if (!inviteToken) return;

        let cancelled = false;
        setEmailInviteModal({ isOpen: true, token: inviteToken, invitation: null, error: null, loading: true });

        getInvitation(inviteToken)
            .then((data) => {
                if (cancelled) return;
                setEmailInviteModal((prev) => ({ ...prev, invitation: data, loading: false }));
            })
            .catch((err) => {
                if (cancelled) return;
                setEmailInviteModal((prev) => ({
                    ...prev,
                    error: err?.response?.data?.message || err?.message || 'Failed to load invitation',
                    loading: false,
                }));
            });

        return () => { cancelled = true; };
    }, [location.search]);

    const cancelAction = () => {
        if (confirmationModal.isSubmitting) return;
        setConfirmationModal({
            isOpen: false,
            token: null,
            campaignId: null,
            action: null,
            isSubmitting: false,
        });
    };


    // renderCampaignCard and renderSection logic has been moved into separate components

    if (loading && !hasLoaded) {
        return <LoadingSpinner message="Loading your campaigns..." />;
    }

    return (
        <div className="w-full">
                <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
                {showComingSoonModal && (
                    <ComingSoonModal
                        featureName="Export functionality"
                        onClose={() => setShowComingSoonModal(false)}
                    />
                )}
                {/* Header Section (aligned with navbar + sidebar layout) */}
                {/* <div className="bg-white shadow-md px-5 py-4 sm:py-5 mb-6"> */}
                <div className="sticky top-0 z-40 w-full bg-white shadow-md px-3 py-2.5 sm:px-4">
                    <div className="flex flex-col md:flex-row md:flex-nowrap justify-between items-start md:items-center gap-2 md:gap-3">
                        {/* Left: Page title & description */}
                        <div className="min-w-0 flex-1">
                            <h1 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 leading-tight">
                                Your Campaigns
                            </h1>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                View invitations and track each campaign from pending to confirmation.
                            </p>
                        </div>

                        {/* Right: Primary actions */}
                        <div className="flex flex-col sm:flex-row sm:flex-nowrap gap-2 w-full md:w-auto shrink-0">
                            {/* Connect Social (Instagram / YouTube) */}
                            <button
                                onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/connect-social-media`)}
                                className="inline-flex items-center justify-center px-2 sm:px-2 py-1.5 rounded-lg border border-orange-500 text-orange-600 bg-white hover:bg-orange-50 hover:border-orange-600 text-xs sm:text-sm font-medium transition-all duration-200 shadow-sm"
                            >
                                <FaExternalLinkAlt className="mr-2 text-xs sm:text-sm" />
                                Connect Social Media
                            </button>

                           <div className="flex flex-row gap-2">
                             {/* Generate Link */}
                             <button
                                onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/generate-link`)}
                                className="inline-flex items-center justify-center px-2 sm:px-2 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 text-xs sm:text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                                <FaExternalLinkAlt className="mr-2 text-xs sm:text-sm" />
                                Generate Link
                            </button>

                            {/* Export */}
                            <button
                                onClick={handleExport}
                                className="inline-flex items-center justify-center px-2 sm:px-2 py-1.5 rounded-lg bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-xs sm:text-sm font-medium transition-all duration-200 shadow-sm"
                            >
                                <FaDownload className="mr-2 text-xs sm:text-sm" />
                                Export
                            </button>
                            
                           </div>
                        </div>
                    </div>
                </div>

                {/* <div className="space-y-6 lg:space-y-8 px-4 pb-8 bg-[#f5f5f7] -mx-0 rounded-none sm:rounded-lg"> */}
                <div className="mt-4 space-y-3 lg:space-y-4 px-3 sm:px-4 pb-8 bg-[#f5f5f7]">
                    {/* Stats Overview */}
                    <DashboardStats counts={counts} />

                    {/* Enhanced Search and Filter Section (collapsible) */}
                    <FilterSection
                        debouncedSearch={debouncedSearch}
                        searchTerm={searchTerm}
                        companyFilter={companyFilter}
                        setCompanyFilter={setCompanyFilter}
                        brandFilter={brandFilter}
                        setBrandFilter={setBrandFilter}
                        availableCompanies={availableCompanies}
                        availableBrands={availableBrands}
                        activeFilters={activeFilters}
                        handleFilterChange={handleFilterChange}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        setSearchTerm={setSearchTerm}
                        setPage={setPage}
                    />

                    {/* Campaign Sections */}
                    <AnimatePresence>
                        {!error && (
                            <>
                                <CampaignSection
                                    title="Pending Invitations"
                                    data={invitations.pending}
                                    icon={<span className="text-lg leading-none" aria-hidden>⌛</span>}
                                    iconColor="transparent"
                                    status="pending"
                                    counts={counts}
                                    loading={loading}
                                    setShowAnalytics={setShowAnalytics}
                                    setCommissionModal={setCommissionModal}
                                    setBrandDetailsModal={setBrandDetailsModal}
                                    copied={copied}
                                    setCopied={setCopied}
                                    selectedCampaign={selectedCampaign}
                                    setSelectedCampaign={setSelectedCampaign}
                                    isDialogOpen={isDialogOpen}
                                    setIsDialogOpen={setIsDialogOpen}
                                    selectedMediaType={selectedMediaType}
                                    setSelectedMediaType={setSelectedMediaType}
                                    youtubeUrls={youtubeUrls}
                                    setYoutubeUrls={setYoutubeUrls}
                                    newYoutubeLink={newYoutubeLink}
                                    setNewYoutubeLink={setNewYoutubeLink}
                                    instagramUrls={instagramUrls}
                                    setInstagramUrls={setInstagramUrls}
                                    newInstagramLink={newInstagramLink}
                                    setNewInstagramLink={setNewInstagramLink}
                                    mediaError={mediaError}
                                    mediaSuccess={mediaSuccess}
                                    mediaLoading={mediaLoading}
                                    handleAddMedia={handleAddMedia}
                                    openConfirmationModal={openConfirmationModal}
                                    toast={toast}
                                />
                                <CampaignSection
                                    title="Accepted Campaigns"
                                    data={invitations.accepted}
                                    icon={<FaCheckCircle />}
                                    iconColor="#10B981"
                                    status="accepted"
                                    counts={counts}
                                    loading={loading}
                                    setShowAnalytics={setShowAnalytics}
                                    setCommissionModal={setCommissionModal}
                                    setBrandDetailsModal={setBrandDetailsModal}
                                    copied={copied}
                                    setCopied={setCopied}
                                    selectedCampaign={selectedCampaign}
                                    setSelectedCampaign={setSelectedCampaign}
                                    isDialogOpen={isDialogOpen}
                                    setIsDialogOpen={setIsDialogOpen}
                                    selectedMediaType={selectedMediaType}
                                    setSelectedMediaType={setSelectedMediaType}
                                    youtubeUrls={youtubeUrls}
                                    setYoutubeUrls={setYoutubeUrls}
                                    newYoutubeLink={newYoutubeLink}
                                    setNewYoutubeLink={setNewYoutubeLink}
                                    instagramUrls={instagramUrls}
                                    setInstagramUrls={setInstagramUrls}
                                    newInstagramLink={newInstagramLink}
                                    setNewInstagramLink={setNewInstagramLink}
                                    mediaError={mediaError}
                                    mediaSuccess={mediaSuccess}
                                    mediaLoading={mediaLoading}
                                    handleAddMedia={handleAddMedia}
                                    openConfirmationModal={openConfirmationModal}
                                    toast={toast}
                                    onCampaignsRefresh={fetchInvitations}
                                />
                                <CampaignSection
                                    title="Rejected Campaigns"
                                    data={invitations.rejected}
                                    icon={<FaTimesCircle />}
                                    iconColor="#EF4444"
                                    status="rejected"
                                    counts={counts}
                                    loading={loading}
                                    setShowAnalytics={setShowAnalytics}
                                    setCommissionModal={setCommissionModal}
                                    setBrandDetailsModal={setBrandDetailsModal}
                                    copied={copied}
                                    setCopied={setCopied}
                                    selectedCampaign={selectedCampaign}
                                    setSelectedCampaign={setSelectedCampaign}
                                    isDialogOpen={isDialogOpen}
                                    setIsDialogOpen={setIsDialogOpen}
                                    selectedMediaType={selectedMediaType}
                                    setSelectedMediaType={setSelectedMediaType}
                                    youtubeUrls={youtubeUrls}
                                    setYoutubeUrls={setYoutubeUrls}
                                    newYoutubeLink={newYoutubeLink}
                                    setNewYoutubeLink={setNewYoutubeLink}
                                    instagramUrls={instagramUrls}
                                    setInstagramUrls={setInstagramUrls}
                                    newInstagramLink={newInstagramLink}
                                    setNewInstagramLink={setNewInstagramLink}
                                    mediaError={mediaError}
                                    mediaSuccess={mediaSuccess}
                                    mediaLoading={mediaLoading}
                                    handleAddMedia={handleAddMedia}
                                    openConfirmationModal={openConfirmationModal}
                                    toast={toast}
                                />
                                <CampaignSection
                                    title="Expired Invitations"
                                    data={invitations.expired}
                                    icon={<FaTimesCircle />}
                                    iconColor="#6B7280"
                                    status="expired"
                                    counts={counts}
                                    loading={loading}
                                    setShowAnalytics={setShowAnalytics}
                                    setCommissionModal={setCommissionModal}
                                    setBrandDetailsModal={setBrandDetailsModal}
                                    copied={copied}
                                    setCopied={setCopied}
                                    selectedCampaign={selectedCampaign}
                                    setSelectedCampaign={setSelectedCampaign}
                                    isDialogOpen={isDialogOpen}
                                    setIsDialogOpen={setIsDialogOpen}
                                    selectedMediaType={selectedMediaType}
                                    setSelectedMediaType={setSelectedMediaType}
                                    youtubeUrls={youtubeUrls}
                                    setYoutubeUrls={setYoutubeUrls}
                                    newYoutubeLink={newYoutubeLink}
                                    setNewYoutubeLink={setNewYoutubeLink}
                                    instagramUrls={instagramUrls}
                                    setInstagramUrls={setInstagramUrls}
                                    newInstagramLink={newInstagramLink}
                                    setNewInstagramLink={setNewInstagramLink}
                                    mediaError={mediaError}
                                    mediaSuccess={mediaSuccess}
                                    mediaLoading={mediaLoading}
                                    handleAddMedia={handleAddMedia}
                                    openConfirmationModal={openConfirmationModal}
                                    toast={toast}
                                />
                            </>
                        )}
                    </AnimatePresence>

                    {/* Enhanced Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center">
                            <nav className="flex items-center space-x-2">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="px-3 sm:px-4 py-2 rounded-lg border border-gray-300 disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200 text-sm sm:text-base font-medium"
                                >
                                    Previous
                                </button>
                                <span className="px-3 sm:px-4 py-2 text-gray-600 text-sm sm:text-base">
                                    Page {page} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="px-3 sm:px-4 py-2 rounded-lg border border-gray-300 disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200 text-sm sm:text-base font-medium"
                                >
                                    Next
                                </button>
                            </nav>
                        </div>
                    )}
                </div>

            {/* Enhanced Confirmation Modal */}
            {confirmationModal.isOpen && (
                <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-sm sm:max-w-md"
                    >
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
                            Confirm {confirmationModal.action === 'accept' ? 'Acceptance' : 'Rejection'}
                        </h2>
                        <p className="text-gray-600 mb-6 text-sm sm:text-base">
                            Are you sure you want to {confirmationModal.action} this campaign?
                        </p>
                        <div className="flex flex-col sm:flex-row justify-end gap-3">
                            <button
                                type="button"
                                disabled={confirmationModal.isSubmitting}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded transition-colors duration-200 text-sm sm:text-base font-medium disabled:opacity-50 disabled:pointer-events-none disabled:hover:bg-gray-200"
                                onClick={cancelAction}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={confirmationModal.isSubmitting}
                                className={`px-4 py-2 text-white rounded transition-colors duration-200 text-sm sm:text-base font-medium disabled:opacity-70 disabled:pointer-events-none ${confirmationModal.action === 'accept'
                                    ? 'bg-green-600 hover:bg-green-700 disabled:hover:bg-green-600'
                                    : 'bg-red-600 hover:bg-red-700 disabled:hover:bg-red-600'
                                    }`}
                                onClick={confirmAction}
                            >
                                {confirmationModal.isSubmitting
                                    ? confirmationModal.action === 'accept'
                                        ? 'Accepting…'
                                        : 'Rejecting…'
                                    : confirmationModal.action === 'accept'
                                        ? 'Accept'
                                        : 'Reject'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {emailInviteModal.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="bg-white rounded-lg shadow-lg w-full max-w-2xl overflow-hidden"
                    >
                        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-100">
                            <div className="min-w-0">
                                <h2 className="text-lg font-semibold text-gray-900 truncate">Campaign Invitation</h2>
                                <p className="text-sm text-gray-500 truncate">Accept or reject the campaign invite.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setEmailInviteModal({ isOpen: false, token: null, invitation: null, error: null, loading: false })}
                                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="p-4">
                            {emailInviteModal.loading ? (
                                <div className="text-sm text-gray-600">Loading invitation...</div>
                            ) : emailInviteModal.error ? (
                                <div className="text-sm text-red-600">{emailInviteModal.error}</div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="text-sm text-gray-700">
                                        Invited email: <span className="font-semibold">{emailInviteModal.invitation?.email}</span>
                                    </div>

                                    <div className="space-y-2">
                                        {(emailInviteModal.invitation?.campaigns || []).map(({ campaign, status }) => (
                                            <div key={campaign?._id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <div className="font-semibold text-gray-900 truncate">{campaign?.name || 'Campaign'}</div>
                                                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">{campaign?.description || ''}</div>
                                                        <div className="text-xs text-gray-500 mt-2">Status: {status}</div>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <button
                                                            type="button"
                                                            disabled={status !== 'pending'}
                                                            onClick={() => openConfirmationModal(emailInviteModal.token, campaign._id, 'accept')}
                                                            className="px-3 py-1 rounded bg-green-600 hover:bg-green-700 text-white text-sm font-medium disabled:opacity-60"
                                                        >
                                                            Accept
                                                        </button>
                                                        <button
                                                            type="button"
                                                            disabled={status !== 'pending'}
                                                            onClick={() => openConfirmationModal(emailInviteModal.token, campaign._id, 'reject')}
                                                            className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-60"
                                                        >
                                                            Reject
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Commission Details Modal */}
            {commissionModal.isOpen && (
                <CommissionDetailsModal
                    isOpen={commissionModal.isOpen}
                    onClose={() => setCommissionModal({ ...commissionModal, isOpen: false })}
                    commission={commissionModal.commission}
                    campaignName={commissionModal.campaignName}
                    campaignType={commissionModal.campaignType}
                    agreementStatus={commissionModal.agreementStatus}
                />
            )}

            {/* Brand Details Modal */}
            {brandDetailsModal.isOpen && (
                <BrandDetailsModal
                    isOpen={brandDetailsModal.isOpen}
                    onClose={() => setBrandDetailsModal({ ...brandDetailsModal, isOpen: false })}
                    companyName={brandDetailsModal.companyName}
                    brandName={brandDetailsModal.brandName}
                    companyEmail={brandDetailsModal.companyEmail}
                    companyPhone={brandDetailsModal.companyPhone}
                />
            )}
            {/* <Footer /> */}
        </div>
    );
};

export default AffiliateDashboard;
